import { useEffect, useState } from 'react';
import { useAccessToken } from '../auth/use-access-token.js';
import { apiFetch } from './client.js';

/**
 * An athlete's videos on one of their lessons (step 7a, ruled 14-16 October).
 * The thumbnail and video links work for an hour; the list is re-read after
 * every send and removal, so they never get old on screen. There is no
 * replace (ruled 16 October): remove one the coach has not opened, send another.
 */

type GetToken = () => Promise<string | null>;

export interface LessonVideo {
  id: string;
  sent_at: string;
  duration_seconds: number | null;
  thumbnail_url: string | null;
  video_url: string;
  opened_at: string | null;
  can_change: boolean;
  athlete_note: string | null;
  coach_reply: string | null;
}

export interface LessonVideos {
  /** This lesson takes videos: the button is drawn. */
  open: boolean;
  places_left: number;
  videos: LessonVideo[];
  /** The coach's videos on this lesson, read only (step 7c). */
  coach_videos: CoachVideo[];
}

export interface CoachVideo {
  id: string;
  sent_at: string;
  duration_seconds: number | null;
  thumbnail_url: string | null;
  video_url: string;
}

/**
 * Where a send goes, and where its strip leads back to: the athlete's lesson
 * page, or the coach's (step 7c).
 */
export interface SendTarget {
  base: string;
  href: string;
  /** "recul": a Recul Save in lesson - the API checks the parent's photo and video consent (ruled 24 October). */
  from?: 'recul';
}

export const athleteTarget = (lessonId: string): SendTarget => ({
  base: `/api/athlete-lessons/${encodeURIComponent(lessonId)}`,
  href: `/my/${lessonId}`,
});

export interface StartedUpload {
  upload_id: string;
  video_url: string;
  thumbnail_url: string;
}

const base = (lessonId: string) => `/api/athlete-lessons/${encodeURIComponent(lessonId)}`;

export function readLessonVideos(getToken: GetToken, lessonId: string): Promise<LessonVideos> {
  return apiFetch(`${base(lessonId)}/videos`, getToken);
}

export function startUpload(
  getToken: GetToken,
  target: SendTarget,
  body: { size_bytes: number },
): Promise<StartedUpload> {
  return apiFetch(`${target.base}/video-uploads`, getToken, { method: 'POST', body: JSON.stringify({ ...body, ...(target.from ? { from: target.from } : {}) }) });
}

export function finishUpload(
  getToken: GetToken,
  target: SendTarget,
  body: { upload_id: string; duration_seconds: number },
): Promise<{ id: string }> {
  return apiFetch(`${target.base}/videos`, getToken, { method: 'POST', body: JSON.stringify({ ...body, ...(target.from ? { from: target.from } : {}) }) });
}

export function setVideoNote(
  getToken: GetToken,
  lessonId: string,
  videoId: string,
  note: string,
): Promise<{ athlete_note: string | null }> {
  return apiFetch(`${base(lessonId)}/videos/${encodeURIComponent(videoId)}/note`, getToken, {
    method: 'PUT',
    body: JSON.stringify({ note }),
  });
}

export function removeLessonVideo(getToken: GetToken, lessonId: string, videoId: string): Promise<void> {
  return apiFetch(`${base(lessonId)}/videos/${encodeURIComponent(videoId)}`, getToken, { method: 'DELETE' });
}

export type LessonVideosState = { status: 'loading' } | { status: 'ready'; data: LessonVideos } | { status: 'failed' };

/** Re-read whenever `nonce` changes: after a send finishes, a removal, a replace. */
export function useLessonVideos(lessonId: string, nonce: number): LessonVideosState {
  const getToken = useAccessToken();
  const [state, setState] = useState<LessonVideosState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    readLessonVideos(getToken, lessonId)
      .then((data) => {
        if (!cancelled) setState({ status: 'ready', data });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'failed' });
      });
    return () => {
      cancelled = true;
    };
  }, [getToken, lessonId, nonce]);

  return state;
}
